import React, { useState } from 'react';
import { ArrowUpRight, Phone, Mail, MapPin, Check } from 'lucide-react';
import { siteData } from '../mock/data';
import ScrollReveal from '../components/ScrollReveal';

const Contact = () => {
  const { company } = siteData;
  const [form, setForm] = useState({
    anrede: '',
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    nachricht: '',
    consent: false,
  });
  const [sent, setSent] = useState(false);

  const handle = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.nachname || !form.email || !form.nachricht || !form.consent) return;
    setSent(true);
    try { localStorage.setItem('planhaus_last_contact', JSON.stringify(form)); } catch (_) { /* noop */ }
  };

  return (
    <>
      {/* HERO */}
      <section className="pt-40 pb-20">
        <div className="container-x">
          <div className="font-mono-up text-[#0A2A28]/60 mb-8 hero-eyebrow">
            <span className="gold-line" />Kontakt
          </div>
          <h1 className="font-display text-[12vw] md:text-[8vw] leading-[0.9] text-[#0A2A28] -tracking-[0.02em]">
            <span className="hero-title block">Kontakt zu</span>
            <span className="hero-title-2 block font-serif-italic text-[#BFA070]">Aurelian Heights</span>
          </h1>
          <div className="grid md:grid-cols-12 gap-10 mt-12 hero-para">
            <div className="md:col-span-6 md:col-start-6">
              <p className="text-[#0A2A28]/80 text-lg leading-relaxed">
                Nehmen Sie gerne Kontakt mit uns auf. Ob telefonisch, per E‑Mail oder über das Kontaktformular. Wir freuen uns, von Ihnen zu hören.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-32">
        <div className="container-x grid md:grid-cols-12 gap-16">
          <div className="md:col-span-7">
            {sent ? (
              <ScrollReveal>
                <div className="border border-[#BFA070] p-14 bg-[#EFE9DE]">
                  <Check size={38} strokeWidth={1.25} className="text-[#BFA070] mb-6" />
                  <h3 className="font-display text-4xl text-[#0A2A28] mb-4">Vielen Dank für Ihre Nachricht.</h3>
                  <p className="text-[#0A2A28]/70 text-lg">Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück.</p>
                </div>
              </ScrollReveal>
            ) : (
              <form onSubmit={submit} className="space-y-8">
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <label className="lux-label">Anrede</label>
                    <select className="lux-input" value={form.anrede} onChange={handle('anrede')}>
                      <option value="">Wählen</option>
                      <option>Herr</option>
                      <option>Frau</option>
                      <option>Divers</option>
                    </select>
                  </div>
                  <div>
                    <label className="lux-label">Vorname</label>
                    <input className="lux-input" type="text" value={form.vorname} onChange={handle('vorname')} />
                  </div>
                  <div>
                    <label className="lux-label">Nachname *</label>
                    <input className="lux-input" type="text" required value={form.nachname} onChange={handle('nachname')} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="lux-label">E‑Mail *</label>
                    <input className="lux-input" type="email" required value={form.email} onChange={handle('email')} />
                  </div>
                  <div>
                    <label className="lux-label">Telefon</label>
                    <input className="lux-input" type="tel" value={form.telefon} onChange={handle('telefon')} />
                  </div>
                </div>
                <div>
                  <label className="lux-label">Nachricht *</label>
                  <textarea className="lux-input resize-none" rows={5} required value={form.nachricht} onChange={handle('nachricht')} />
                </div>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={form.consent} onChange={handle('consent')} required className="mt-1 accent-[#BFA070] w-4 h-4" />
                  <span className="text-sm text-[#0A2A28]/70 leading-relaxed">
                    Ich bin mit der Verwendung meiner Daten gemäß der <a href="#" className="underline hover:text-[#BFA070]">Datenschutzerklärung</a> einverstanden. *
                  </span>
                </label>
                <button type="submit" className="lux-btn">
                  <span>Senden</span>
                  <ArrowUpRight className="arrow" size={16} strokeWidth={1.5} />
                </button>
              </form>
            )}
          </div>

          <aside className="md:col-span-5 space-y-10">
            <ScrollReveal>
              <div className="border border-[#0A2A28]/12 p-10 bg-[#EFE9DE]/60">
                <div className="font-mono-up text-[#0A2A28]/55 mb-5">Aurelian Heights vor Ort</div>
                <h3 className="font-display text-3xl text-[#0A2A28] mb-6">{company.address.line1}</h3>
                <div className="flex items-start gap-3 text-[#0A2A28]/75">
                  <MapPin size={18} strokeWidth={1.25} className="mt-1 text-[#BFA070]" />
                  <div>
                    <div>{company.address.line2}</div>
                    <div>{company.address.line3}</div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={1}>
              <a href={`tel:${company.phoneHref}`} className="block group border-t border-[#0A2A28]/15 pt-6">
                <div className="font-mono-up text-[#0A2A28]/55 mb-3">Anrufen</div>
                <div className="flex items-center gap-3 font-display text-2xl md:text-3xl text-[#0A2A28] group-hover:text-[#BFA070] transition-colors">
                  <Phone size={20} strokeWidth={1.25} /> {company.phone}
                </div>
              </a>
            </ScrollReveal>

            <ScrollReveal delay={2}>
              <a href={`mailto:${company.email}`} className="block group border-t border-[#0A2A28]/15 pt-6">
                <div className="font-mono-up text-[#0A2A28]/55 mb-3">Schreiben</div>
                <div className="flex items-center gap-3 font-display text-2xl md:text-3xl text-[#0A2A28] group-hover:text-[#BFA070] transition-colors">
                  <Mail size={20} strokeWidth={1.25} /> {company.email}
                </div>
              </a>
            </ScrollReveal>

            <ScrollReveal delay={3}>
              <div className="relative aspect-[4/3] overflow-hidden border border-[#0A2A28]/10">
                <iframe
                  title="Karte"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=6.84%2C51.29%2C6.86%2C51.31&amp;layer=mapnik&amp;marker=51.30%2C6.85"
                  className="w-full h-full grayscale contrast-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2A28]/10 to-transparent pointer-events-none" />
              </div>
            </ScrollReveal>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Contact;
